import { useState, useEffect } from "react";
import { useNavigationType, useNavigate, useLocation } from "react-router-dom";
import { useShallow } from "zustand/shallow";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  ChipProps,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Typography,
} from "@mui/material";
import { mdiAccountPlusOutline, mdiCheck, mdiClose, mdiDoor } from "@mdi/js";
import Icon from "@mdi/react";

import AsyncAvatar from "components/AsyncAvatar";
import ChipList from "components/ChipList";
import DataIconPlaceholder from "components/DataIconPlaceholder";
import FAB from "components/FAB";
import SearchInput from "components/SearchInput";
import VirtualizedList from "components/VirtualizedList";
import { StorageBuckets } from "api/storage";
import { GetUserById } from "api/profile";
import { RoomManagerData, RoomManagerRoom } from "api/room-manager";
import getNameInitials from "utils/getNameInitials";
import useBoundStore from "store";

export interface RoomsProps {
  data: RoomManagerData;
  isAdmin: boolean;
  participants: GetUserById[];
  /** If true, all assignment controls are hidden. Used for timeline/archived events */
  viewMode?: boolean;
  onSave: (data: RoomManagerData) => void;
}

export default function Rooms(props: RoomsProps) {
  const { data, isAdmin, participants, viewMode = false, onSave } = props;

  const [assigningRoom, setAssigningRoom] = useState<RoomManagerRoom | null>(null);
  const [selectedUsers, setSelectedUsers] = useState<GetUserById[]>([]);
  const [searchString, setSearchString] = useState("");
  const [searchedUsers, setSearchedUsers] = useState<GetUserById[]>([]);

  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();

  const { isMobileDevice, smallAvatars } = useBoundStore(
    useShallow((state) => ({
      isMobileDevice: state.miscellaneous.isMobileDevice,
      smallAvatars: state.miscellaneous.smallAvatars,
    }))
  );

  // ── Assignment dialog handlers ─────────────────────────────────────────

  function openAssignDialog(room: RoomManagerRoom) {
    setAssigningRoom(room);
    setSelectedUsers([]);
    setSearchString("");
    setSearchedUsers([]);
    navigate(location.pathname);
  }

  function closeAssignDialogUI() {
    setAssigningRoom(null);
    setSelectedUsers([]);
    setSearchString("");
    setSearchedUsers([]);
  }

  function closeAssignDialog() {
    closeAssignDialogUI();
    navigate(-1);
  }

  /**
   * On pressing back button close opened dialog
   */
  useEffect(() => {
    if (navigationType === "POP") {
      closeAssignDialogUI();
    }
  }, [navigationType]);

  function handleSearchUsers(newSearchString: string, results: GetUserById[]) {
    setSearchString(newSearchString);
    const selectedIds = selectedUsers.map((u) => u.uid);
    setSearchedUsers(results.filter((u) => !selectedIds.includes(u.uid)));
  }

  function handleSelectUser(user: GetUserById) {
    setSelectedUsers((prev) => [...prev, user]);
    setSearchString("");
  }

  function handleRemoveSelectedUser(user: GetUserById) {
    setSelectedUsers((prev) => prev.filter((u) => u.uid !== user.uid));
  }

  function handleConfirmAssign() {
    if (!assigningRoom) return;

    const newUserIds = [
      ...assigningRoom.userIds,
      ...selectedUsers.map((u) => u.uid).filter((uid) => !assigningRoom.userIds.includes(uid)),
    ];

    const updatedRooms = data.rooms.map((r) => (r.id === assigningRoom.id ? { ...r, userIds: newUserIds } : r));
    onSave({ ...data, rooms: updatedRooms });
    closeAssignDialog();
  }

  function handleRemoveUserFromRoom(room: RoomManagerRoom, userId: number) {
    const updatedRooms = data.rooms.map((r) =>
      r.id === room.id ? { ...r, userIds: r.userIds.filter((uid) => uid !== userId) } : r
    );
    onSave({ ...data, rooms: updatedRooms });
  }

  // ── Room card builders ────────────────────────────────────────────────

  function buildRoomCards(rooms: RoomManagerRoom[]): JSX.Element[] {
    return rooms.map((room) => {
      const roomParticipants = participants.filter((p) => room.userIds.includes(p.uid));
      const occupancyLabel =
        room.capacity !== null
          ? `${roomParticipants.length}/${room.capacity} ${room.capacity === 1 ? "person" : "people"}`
          : `${roomParticipants.length} ${roomParticipants.length === 1 ? "person" : "people"}`;

      const isRoomFull = room.capacity !== null && roomParticipants.length >= room.capacity;
      const availableSpaces = room.capacity !== null ? Math.max(0, room.capacity - roomParticipants.length) : null;

      return (
        <Card key={room.id} sx={{ mx: 2, mb: 2 }}>
          <CardHeader
            avatar={
              <Box color='primary.main' display='flex' alignItems='center'>
                <Icon path={mdiDoor} size={1.25} title='Room' />
              </Box>
            }
            title={room.name}
            subheader={
              <Box display='flex' alignItems='center' gap={0.5} flexWrap='wrap'>
                <Typography variant='caption' color='text.secondary'>
                  {occupancyLabel}
                </Typography>
              </Box>
            }
            action={
              !viewMode && isAdmin && !isRoomFull ? (
                <IconButton size='small' onClick={() => openAssignDialog(room)} title='Assign users'>
                  <Icon path={mdiAccountPlusOutline} size={1} />
                </IconButton>
              ) : undefined
            }
          />
          <Divider />
          <CardContent>
            <Box display='flex' flexWrap='wrap' gap={1}>
              {roomParticipants.map((user) => (
                <UserChip
                  key={user.uid}
                  avatarFile={user.avatar ? smallAvatars[user.avatar] : undefined}
                  avatarName={user.avatar}
                  name={user.name ?? "-"}
                  onDelete={!viewMode && isAdmin ? () => handleRemoveUserFromRoom(room, user.uid) : undefined}
                />
              ))}

              {availableSpaces !== null &&
                Array.from({ length: availableSpaces }).map((_, index) => (
                  <Chip key={`empty-${index}`} disabled label='Empty' variant='outlined' />
                ))}
            </Box>
          </CardContent>
        </Card>
      );
    });
  }

  // ── Render room list grouped by zone ──────────────────────────────────

  function renderRooms(): JSX.Element {
    if (data.rooms.length === 0) {
      return <DataIconPlaceholder text='No rooms have been set up yet' icon={mdiDoor} />;
    }

    const hasZones = data.zones.length > 0;

    if (!hasZones) {
      return <Box pt={2}>{buildRoomCards(data.rooms)}</Box>;
    }

    const sections: JSX.Element[] = [];

    // Rooms without a zone first
    const ungroupedRooms = data.rooms.filter((r) => !r.zoneId);
    if (ungroupedRooms.length > 0) {
      sections.push(
        <Box key='ungrouped' pt={2}>
          {buildRoomCards(ungroupedRooms)}
        </Box>
      );
    }

    // Rooms grouped by zone
    data.zones.forEach((zone) => {
      const zoneRooms = data.rooms.filter((r) => r.zoneId === zone.id);
      if (zoneRooms.length === 0) return;

      sections.push(
        <Box key={zone.id}>
          <Typography variant='overline' color='text.secondary' sx={{ px: 2, pt: 2, pb: 0.5, display: "block" }}>
            {zone.name}
          </Typography>
          {buildRoomCards(zoneRooms)}
        </Box>
      );
    });

    return <Box>{sections}</Box>;
  }

  // ── Assignment dialog user list ───────────────────────────────────────

  function buildAssignDialogList(): JSX.Element[] {
    if (!assigningRoom) return [];

    const alreadyInRoom = assigningRoom.userIds;
    const selectedIds = selectedUsers.map((u) => u.uid);

    // Get all users assigned to other rooms
    const assignedToOtherRooms = data.rooms.filter((r) => r.id !== assigningRoom.id).flatMap((r) => r.userIds);

    // Calculate remaining capacity
    const currentOccupancy = alreadyInRoom.length + selectedUsers.length;
    const isFull = assigningRoom.capacity !== null && currentOccupancy >= assigningRoom.capacity;

    const available = participants.filter(
      (p) => !alreadyInRoom.includes(p.uid) && !selectedIds.includes(p.uid) && !assignedToOtherRooms.includes(p.uid)
    );
    const toRender =
      searchString === ""
        ? available
        : searchedUsers.filter((u) => !alreadyInRoom.includes(u.uid) && !assignedToOtherRooms.includes(u.uid));

    const items: JSX.Element[] = [];

    if (selectedUsers.length > 0) {
      items.push(
        <Box key='chips' maxHeight='30vh' overflow='auto' px={2}>
          <ChipList chips={selectedUsers} onDelete={handleRemoveSelectedUser} />
        </Box>
      );
    }

    if (!isFull) {
      items.push(
        <Box key='search' mt={1}>
          <SearchInput
            data={available as unknown as Record<string, unknown>[]}
            onSearchedResults={(str, results) => handleSearchUsers(str, results as unknown as GetUserById[])}
            inputProps={{
              "aria-label": "Search participants",
              placeholder: "Search participants",
            }}
            searchBy='name'
            searchString={searchString}
          />
          <Divider />
        </Box>
      );
    }

    if (isFull) {
      items.push(
        <Box key='room-full' p={2}>
          <Typography align='center' color='text.secondary'>
            Room is at full capacity ({assigningRoom.capacity} {assigningRoom.capacity === 1 ? "person" : "people"})
          </Typography>
        </Box>
      );
    } else if (toRender.length === 0 && searchString !== "") {
      items.push(
        <Box key='no-results' p={2}>
          <Typography align='center' color='text.secondary'>
            No participants found
          </Typography>
        </Box>
      );
    } else if (toRender.length === 0 && searchString === "" && available.length === 0) {
      items.push(
        <Box key='all-assigned' p={2}>
          <Typography align='center' color='text.secondary'>
            All available participants are assigned
          </Typography>
        </Box>
      );
    } else {
      toRender.forEach((user) => {
        items.push(
          <ListItemButton key={user.uid} onClick={() => handleSelectUser(user)} disabled={isFull}>
            <ListItemAvatar>
              <AsyncAvatar
                avatarFile={user.avatar ? smallAvatars[user.avatar] : undefined}
                avatarName={user.avatar}
                placeholder={getNameInitials(user.name, 1)}
                bucket={StorageBuckets.PROFILES_AVATARS}
              />
            </ListItemAvatar>
            <ListItemText primary={user.name} secondary={user.tag} />
          </ListItemButton>
        );
      });
    }

    return items;
  }

  return (
    <>
      {renderRooms()}

      {/* ── Assignment dialog ── */}
      <Dialog
        fullScreen={isMobileDevice}
        maxWidth='sm'
        fullWidth
        open={Boolean(assigningRoom)}
        onClose={closeAssignDialog}
        PaperProps={isMobileDevice ? { sx: { overflow: "hidden", borderRadius: 0 } } : undefined}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          Assign to &ldquo;{assigningRoom?.name}&rdquo;
          <IconButton onClick={closeAssignDialog} size='small'>
            <Icon path={mdiClose} size={1} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0, overflow: "hidden", position: "relative" }}>
          <VirtualizedList
            estimateSize={56}
            height={isMobileDevice ? "calc(100vh - 136px)" : "400px"}
            rows={buildAssignDialogList()}
          />
        </DialogContent>

        <FAB
          aria-label='Confirm assignment'
          icon={mdiCheck}
          title='Assign'
          loading={false}
          disabled={selectedUsers.length === 0}
          onClick={handleConfirmAssign}
        />
      </Dialog>
    </>
  );
}

// ── Chip sub-components ───────────────────────────────────────────────────

interface UserChipProps {
  avatarFile?: Blob;
  avatarName: string | null;
  name: string;
  onDelete?: ChipProps["onDelete"];
}

function UserChip(props: UserChipProps) {
  const { avatarFile, avatarName, name, onDelete } = props;
  return (
    <Chip
      avatar={
        <AsyncAvatar
          avatarFile={avatarFile}
          avatarName={avatarName}
          placeholder={getNameInitials(name, 1)}
          bucket={StorageBuckets.PROFILES_AVATARS}
        />
      }
      label={name}
      variant='outlined'
      onDelete={onDelete}
    />
  );
}
